import { isEmpty, isEqual, pick } from 'lodash';
import {
  APIFieldType,
  APIReportAttributes,
  APISearchResponse,
  ArdoqId,
  BooleanOperator,
  DataSourceType,
  isAdvancedSearch,
  isGremlinSearch,
  NonPersistedReport,
  Operator,
  QueryBuilderQuery,
  Report,
  ReportColumn,
  ReportColumnType,
  ReportSearchAggregatesArgs,
  ReportSearchArgs,
  SearchContext,
  SortOrder,
} from '@ardoq/api-types';
import { AdHocSearchReport } from './types';
import {
  EMPTY_COLUMN_CUSTOM_NAME,
  ReportTemplate,
  RESETED_COLUMN_CUSTOM_NAME,
  UpdatedColumnNames,
} from './reportTemplateTypes';
import { ReportDataUsedForSearch } from './sections/types/types';
import { isTruthy, Maybe } from '@ardoq/common-helpers';
import { DEFAULT_ARDOQ_I18NLOCALE, type I18nLocale, t } from '@ardoq/i18n';
import { permissionContextIsModified } from '@ardoq/manage-resource-permissions/permission-context';
import type { PermissionContextState } from '@ardoq/manage-resource-permissions';
import {
  EnhancedSearchResponse,
  getColumnCustomLabel,
  getColumnOriginalLabel,
  isColumnWithCustomLabel,
  isComponentRow,
  isReferenceRow,
  mergeDateRangeFieldsInColumns,
  NUMBER_OF_ROWS_PER_PAGE,
  ReportSort,
} from '@ardoq/report-reader';

const hasReportDataChangedSinceLastSearch = (
  report: ReportTemplate,
  reportDataUsedForSearch: Maybe<ReportDataUsedForSearch>
): boolean =>
  Boolean(
    reportDataUsedForSearch &&
    !isEqual(
      pick(report, [
        'workspaceIds',
        'gremlinQuery',
        'advancedSearchQuery',
        'datasource',
      ]),
      reportDataUsedForSearch
    )
  );

const hasWorkspaceSelectionChangedSinceLastSearch = (
  report: ReportTemplate,
  reportDataUsedForSearch: Maybe<ReportDataUsedForSearch>
): boolean =>
  Boolean(
    reportDataUsedForSearch &&
    !isEqual(report.workspaceIds, reportDataUsedForSearch.workspaceIds)
  );

// TODO[report-refactor]: avoid passing isValidAdvancedSearch and validate query here directly
const isValidQuery = (
  report: ReportTemplate,
  isValidAdvancedSearch: boolean
): boolean =>
  (isValidAdvancedSearch && report.advancedSearchQuery !== null) ||
  isGremlinSearchBased(report);

// TODO[report-refactor] create AdvancedSearchReportTemplate and GremlinReportTemplate types and make this a type guard
const isAdvancedSearchBased = ({
  datasource,
}: NonPersistedReport | ReportTemplate | AdHocSearchReport): boolean =>
  isAdvancedSearch(datasource);

const isGremlinSearchBased = ({
  datasource,
}: NonPersistedReport | ReportTemplate | AdHocSearchReport): boolean =>
  isGremlinSearch(datasource);

const getEmptyState = (): ReportTemplate => ({
  name: '',
  description: '',
  datasource: DataSourceType.ADVANCED_SEARCH,
  workspaceIds: [],
  surveyId: null,
  columns: [],
  updatedColumnNames: {},
  selectedAggregate: null,
  advancedSearchQuery: {
    condition: BooleanOperator.AND,
    rules: [
      {
        field: 'type',
        id: 'type',
        input: 'select',
        operator: Operator.EQUAL,
        type: 'string',
        value: SearchContext.COMPONENT,
      },
      {
        condition: BooleanOperator.AND,
        rules: [],
      },
    ],
  },
  gremlinQuery: '',
  query: null,
});

const toUnsavedCopy = (report: Report, name: string): NonPersistedReport => ({
  ...pick(report, [
    'description',
    'datasource',
    'surveyId',
    'workspaceIds',
    'columns',
    'query',
    'selectedAggregate',
    'aiPrompt',
  ]),
  name,
});

export type InitialStateSource =
  | { kind: 'persisted'; report: Report }
  | { kind: 'copy'; report: NonPersistedReport }
  | { kind: 'adHoc'; report: AdHocSearchReport }
  | { kind: 'empty' };

const withExistingQuery = (
  report: Report | NonPersistedReport | AdHocSearchReport
): ReportTemplate => {
  const existingQuery = isAdvancedSearchBased(report)
    ? { advancedSearchQuery: report.query as QueryBuilderQuery }
    : isGremlinSearchBased(report)
      ? { gremlinQuery: report.query as string }
      : null;

  const reportTemplate = {
    ...getEmptyState(),
    ...report,
    ...(existingQuery ?? {}),
  };

  return {
    ...reportTemplate,
    workspaceIds: reportTemplate.workspaceIds ?? [],
  };
};

const getInitialState = (source: InitialStateSource): ReportTemplate => {
  if (source.kind === 'empty') {
    return { ...getEmptyState(), workspaceIds: [] };
  }
  return withExistingQuery(source.report);
};

const isValidDataSelection = ({ workspaceIds }: ReportTemplate) =>
  !isEmpty(workspaceIds);

const getColumnsWithUpdatedNames = (
  reportTemplate: ReportTemplate
): ReportColumn[] => {
  const { columns, updatedColumnNames } = reportTemplate;
  if (isEmpty(updatedColumnNames)) {
    return columns;
  }

  return columns.map(column => {
    let newName = updatedColumnNames[getUpdatedColumnNameKey(column)];
    newName = newName ? newName.trim() : newName;

    const originalName = getColumnOriginalLabel(column);

    if (
      (newName === RESETED_COLUMN_CUSTOM_NAME ||
        newName === EMPTY_COLUMN_CUSTOM_NAME ||
        newName === originalName) &&
      isColumnWithCustomLabel(column)
    ) {
      return {
        ...column,
        originalLabel: originalName,
        label: originalName,
      };
    } else if (newName && newName !== originalName) {
      return {
        ...column,
        originalLabel: originalName,
        label: newName,
      };
    }
    return column;
  });
};

const validateColumnCustomName = (
  currentColumn: ReportColumn,
  columns: ReportColumn[],
  i18nLocale: I18nLocale
): Maybe<string> => {
  const customName = getColumnCustomLabel(currentColumn);

  if (!customName) {
    return null;
  }

  const conflictingColumn = columns.find(column => {
    if (column.key !== currentColumn.key) {
      // a column's custom name is not allowed to be the same as other original names
      // a column's custom name is also not allowed to be the same as other custom names
      return (
        customName === getColumnOriginalLabel(column) ||
        customName === getColumnCustomLabel(column)
      );
    }
  });

  return conflictingColumn
    ? customName === getColumnOriginalLabel(conflictingColumn)
      ? t(
          i18nLocale,
          'reportBuilder:dataPresentationSection.columnRename.errors.nameNotAvailable',
          'This name is not available'
        )
      : customName === getColumnCustomLabel(conflictingColumn)
        ? t(
            i18nLocale,
            'reportBuilder:dataPresentationSection.columnRename.errors.nameInUse',
            'This name is already in use, choose another'
          )
        : null
    : null;
};

const getColumnsRenameErrors = (
  reportTemplate: ReportTemplate,
  i18nLocale: I18nLocale = DEFAULT_ARDOQ_I18NLOCALE
): Record<string, string> => {
  const columnsWithUpdatedNames = getColumnsWithUpdatedNames(reportTemplate);
  const columnsWithCustomNames = columnsWithUpdatedNames.filter(
    isColumnWithCustomLabel
  );

  return columnsWithCustomNames.reduce<Record<string, string>>(
    (acc, columnWithCustomName) => {
      const errorMessage = validateColumnCustomName(
        columnWithCustomName,
        columnsWithUpdatedNames,
        i18nLocale
      );
      if (errorMessage) {
        acc[columnWithCustomName.key] = errorMessage;
      }
      return acc;
    },
    {}
  );
};

const getUpdatedColumnNamesThatAreValidForTheSelectedColumns = (
  { updatedColumnNames }: ReportTemplate,
  selectedColumns: ReportColumn[]
): UpdatedColumnNames => {
  if (isEmpty(updatedColumnNames)) {
    return updatedColumnNames;
  }
  return Object.keys(updatedColumnNames).reduce<UpdatedColumnNames>(
    (acc, key) => {
      if (
        selectedColumns.some(column => getUpdatedColumnNameKey(column) === key)
      ) {
        acc[key] = updatedColumnNames[key];
      }
      return acc;
    },
    {}
  );
};

const hasUnsavedProgress = (
  report: ReportTemplate,
  savedReport: Maybe<Report>,
  permissionContextState: PermissionContextState
): boolean => {
  if (!savedReport) return true;
  const attributesToCompare: Array<keyof ReportTemplate> = [
    'name',
    'datasource',
    'description',
    'columns',
    'selectedAggregate',
  ];

  const hasBasicAttributesChanged = !isEqual(
    pick(report, attributesToCompare),
    pick(savedReport, attributesToCompare)
  );

  const hasQueryChanged = !isEqual(
    report[
      isAdvancedSearchBased(report) ? 'advancedSearchQuery' : 'gremlinQuery'
    ],
    savedReport.query
  );

  const hasWorkspacesChanged =
    !(isEmpty(report.workspaceIds) && isEmpty(savedReport.workspaceIds)) && // some older reports has workspaceIds as null. In the report template it's set to {}. This should not count as a change that should be saved
    !isEqual(report.workspaceIds, savedReport.workspaceIds);

  const hasNewRenames = !isEqual(
    reportBuilderOperations.getColumnsWithUpdatedNames(report),
    report.columns
  );

  const hasPermissionsChanged = permissionContextIsModified(
    permissionContextState.initialPermissionContext,
    permissionContextState.permissionContext
  );

  return Boolean(
    hasBasicAttributesChanged ||
    hasQueryChanged ||
    hasWorkspacesChanged ||
    hasNewRenames ||
    hasPermissionsChanged
  );
};

const getSortKeyAndOrder = (
  report: ReportTemplate | APIReportAttributes | Report | APISearchResponse
): ReportSort => {
  const sortedColumn = report.columns.find(column => column.sort);
  const columnKey = sortedColumn?.key ?? null;
  const order = sortedColumn?.sort ?? SortOrder.ASC;
  return { columnKey, order };
};

const toSearchArgs = ({
  reportTemplate,
  columnKey = null,
  order,
  reportId,
}: {
  reportTemplate: ReportTemplate;
  columnKey?: Maybe<string>; // if search was triggered as a result of sorting, use these params
  order?: SortOrder;
  reportId?: ArdoqId;
}): ReportSearchArgs & {
  reportTemplate: ReportTemplate;
  reportId?: ArdoqId;
} => ({
  reportTemplate,
  reportId,
  query: reportBuilderOperations.isAdvancedSearchBased(reportTemplate)
    ? reportTemplate.advancedSearchQuery
    : reportTemplate.gremlinQuery,
  workspaceIds: reportTemplate.workspaceIds,
  datasource: reportTemplate.datasource,
  sort: columnKey,
  order,
  from: 0,
  size: NUMBER_OF_ROWS_PER_PAGE,
});

const toSearchAggregatesArgs = ({
  reportTemplate,
  reportId,
}: {
  reportTemplate: ReportTemplate;
  reportId?: ArdoqId;
}): ReportSearchAggregatesArgs & {
  reportTemplate: ReportTemplate;
  reportId?: ArdoqId;
} => ({
  reportTemplate,
  reportId,
  query: reportBuilderOperations.isAdvancedSearchBased(reportTemplate)
    ? reportTemplate.advancedSearchQuery
    : reportTemplate.gremlinQuery,
  workspaceIds: reportTemplate.workspaceIds,
  datasource: reportTemplate.datasource,
});

const COMPONENT_REPRESENTATION_KEY = 'name';
const REFERENCE_REPRESENTATION_KEYS = ['source', 'type', 'target'];
const getSelectedColumnsThatAreValidForTheSearchResult = (
  reportTemplate: ReportTemplate,
  apiResponse: EnhancedSearchResponse
): ReportColumn[] => {
  const response: EnhancedSearchResponse = !isEmpty(apiResponse.goneColumns) // goneColumns is quite a rare case so no need to spread all the time
    ? {
        ...apiResponse,
        columns: [...apiResponse.columns, ...apiResponse.goneColumns], // merge selected columns with those that were selected but now missing
      }
    : apiResponse;

  if (response.columns.length === 1) {
    // If there is only one column, i.e. 'path', make that fixed and preselected
    const reportTemplateColumn = reportTemplate.columns.find(
      ({ key }) => response.columns[0].key === key
    );
    return response.columns.map(column => ({
      ...column,
      ...(reportTemplateColumn?.sort
        ? { sort: reportTemplateColumn?.sort }
        : {}), // when we set the column from the response, we can lose information about which column is sorted. We need to preserve that information
    }));
  }
  const resultHasComponents = response.results.find(({ ardoq }) =>
    isComponentRow(ardoq)
  );
  const resultHasReferences = response.results.find(({ ardoq }) =>
    isReferenceRow(ardoq)
  );

  // Replace selected columns with what search returned now, preserving user's order
  // and removing those that no longer exist
  const validColumns = reportTemplate.columns
    .map(selectedColumn => {
      const responseColumn = response.columns.find(
        newColumn => newColumn.key === selectedColumn.key
      );
      if (responseColumn) {
        return selectedColumn.sort // when we set the column from the response, we can lose information about which column is sorted. We need to preserve that information
          ? { ...responseColumn, sort: selectedColumn.sort }
          : responseColumn;
      }
    })
    .filter(isTruthy);

  if (!resultHasReferences && !resultHasComponents && !validColumns.length) {
    // Preselect all columns if we don't know if there are components or references in the result and there are no selected columns
    return response.columns;
  }

  if (!validColumns.length) {
    // if no columns are selected we preselect some columns for the user
    return [
      ...(resultHasComponents // Add name column as preselected
        ? [
            response.columns.find(
              ({ key }) => key === COMPONENT_REPRESENTATION_KEY
            ),
          ]
        : []),
      ...(resultHasReferences
        ? REFERENCE_REPRESENTATION_KEYS.map(
            refKey => response.columns.find(({ key }) => key === refKey) // Add reference representation keys as preselected
          )
        : []),
      ...validColumns,
    ].filter(isTruthy);
  }
  return validColumns;
};

const hasNoColumns = (report: Report | ReportTemplate): boolean =>
  report.columns.length === 0;

const fromCollection = (
  byId: Record<ArdoqId, APIReportAttributes>,
  reportId: string | undefined
): Maybe<Report> => (reportId && byId[reportId] ? byId[reportId] : null);

const mergeDateRangeFieldsInReport = (
  { columns, ...report }: Report,
  isDateRangeColumn: (column: ReportColumn) => boolean
): Report => ({
  ...report,
  columns: mergeDateRangeFieldsInColumns(columns, isDateRangeColumn),
});

const isValidReport = (report: ReportTemplate): boolean => {
  const isAdvancedSearchUsed =
    reportBuilderOperations.isAdvancedSearchBased(report);

  const query = isAdvancedSearchUsed
    ? report.advancedSearchQuery
    : report.gremlinQuery;

  const isValidDataSelection =
    reportBuilderOperations.isValidDataSelection(report);

  const isEmptyResults = reportBuilderOperations.hasNoColumns(report);

  const columnRenameErrors = getColumnsRenameErrors(report);

  return Boolean(
    query &&
    report.datasource &&
    isValidDataSelection &&
    isEmpty(columnRenameErrors) &&
    report.name &&
    !isEmptyResults
  );
};

const hasGoneColumnsSelected = (
  reportTemplate: ReportTemplate,
  goneColumns: ReportColumn[]
): boolean => {
  const goneColumnKeys = goneColumns.map(col => col.key);
  return reportTemplate.columns.some(col => goneColumnKeys.includes(col.key));
};

const getFieldsInColumns = (report: ReportTemplate): ReportColumn[] =>
  report.columns.filter(column => column.type === ReportColumnType.FIELD);

/** Custom Gremlin queries (with `.project`) are easily mis-written and
 * return values as single-element arrays instead just the values. We make a reasonable
 * effort to detect that and warn the user.
 * (It could be argued that the backend should do this, but this was simpler.)
 */
const hasGremlinColumnDataTypeMismatch = (
  searchResults: Maybe<EnhancedSearchResponse>
): boolean => {
  const firstResult = searchResults?.results[0];
  if (!firstResult) return false;

  return searchResults.columns
    .filter(column => column.type === ReportColumnType.CUSTOM)
    .some(column => {
      const value = firstResult[column.key];
      return (
        Array.isArray(value) &&
        value.length === 1 &&
        column.dataType !== APIFieldType.SELECT_MULTIPLE_LIST
      );
    });
};

// To ensure uniqueness, as a normal field column might have the same key as reference column
// NOTE: keeping this function here because moving it to utils creates a circular dependency
// Exporting it on its own because it's not a report builder operation per se
export const getUpdatedColumnNameKey = (column: ReportColumn): string =>
  `${column.key}_${column.type}`;

export const reportBuilderOperations = {
  hasReportDataChangedSinceLastSearch,
  hasWorkspaceSelectionChangedSinceLastSearch,
  isValidQuery,
  isValidReport,
  isAdvancedSearchBased,
  isGremlinSearchBased,
  getEmptyState,
  getInitialState,
  toUnsavedCopy,
  hasUnsavedProgress,
  getSortKeyAndOrder,
  isValidDataSelection,
  toSearchAggregatesArgs,
  toSearchArgs,
  getSelectedColumnsThatAreValidForTheSearchResult,
  hasNoColumns,
  fromCollection,
  mergeDateRangeFieldsInReport,
  getColumnsRenameErrors,
  getColumnsWithUpdatedNames,
  getUpdatedColumnNamesThatAreValidForTheSelectedColumns,
  hasGoneColumnsSelected,
  getFieldsInColumns,
  hasGremlinColumnDataTypeMismatch,
};
